import { GraphQLError } from 'graphql';

import { isAppDebug } from '@/utils/appDebug';
import { deriveUpstreamDiagnostics } from '@/utils/upstreamDiagnostics';

const DEFAULT_ERROR_CODE = 'API_DATA_ERROR';

// Opt-in code for reads whose caller degrades gracefully on failure. Deliberately
// distinct from `API_DATA_ERROR`, which every data source inherits by default.
export const FALLBACK_COVERED_DATA_ERROR = 'FALLBACK_COVERED_DATA_ERROR';

/**
 * Throw a GraphQLError
 * @param error - error message; keep it static — put variable context in `opts`, not the message
 * @param opts - options object
 * @param opts.errorCode - custom error code
 * @param opts.response - full API response, attached only when APP_DEBUG is enabled; bounded
 *   `extensions.upstream` diagnostics (status, path, errorCode) are always derived from it
 * @param opts.upstreamPath - upstream request path, when the response does not carry its URL
 */
export default function throwGraphQLError(
    error: string,
    opts?: { errorCode?: string; response?: unknown; upstreamPath?: string },
): never {
    const extensions: Record<string, unknown> = {
        code: opts?.errorCode || DEFAULT_ERROR_CODE,
    };
    const upstream = deriveUpstreamDiagnostics(opts?.response, opts?.upstreamPath);
    if (upstream) {
        extensions.upstream = upstream;
    }
    if (isAppDebug() && opts?.response) {
        extensions.apiResponse = opts.response;
    }
    throw new GraphQLError(error, { extensions });
}
